"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Recycle, Sparkles, CircleCheck as CheckCircle2, ShieldCheck, Gauge, SlidersHorizontal as Sliders } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const solutions = [{
  id: "resolve-core",
  title: "ReSolve HPLC Solvent Recovery System",
  category: "Solvent Recycling",
  badge: "Featured System",
  description: "Automated, in-line HPLC mobile phase recovery system reducing solvent consumption and hazardous waste by up to 90% in isocratic and selected gradient applications.",
  specs: "Up to 90% solvent reuse · Zero software footprint",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hplc_resolve_system.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hplc_resolve_system.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hplc_resolve_system.png-wvc-sizes",
  link: "/HplcSolventRecoveryResolvePage"
}, {
  id: "process-guardian",
  title: "Process Guardian Integration",
  category: "Online Monitoring",
  badge: "Process PAT",
  description: "Multi-channel optical monitoring platform designed to verify effluent purity and automate solvent diversion valves with real-time feedback.",
  specs: "Continuous inline detection · GMP compliant",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_guardian_system.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_guardian_system.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_guardian_system.png-wvc-sizes",
  link: "/ProcessGuardianPage"
}, {
  id: "chromatography-services",
  title: "Chromatography Method Feasibility",
  category: "Application Support",
  badge: "Technical Service",
  description: "Expert laboratory testing to evaluate solvent recovery rates on your specific HPLC methods before capital commitment.",
  specs: "Full empirical report · ROI projection included",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/lab_chromatography_testing.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/lab_chromatography_testing.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/lab_chromatography_testing.png-wvc-sizes",
  link: "/FeasibilityStudiesPage"
}];
const highlights = [{
  iconName: "recycle",
  title: "Cut Solvent Spend by 80–90%",
  detail: "Recycle uncontaminated baseline mobile phases straight back to solvent reservoirs automatically."
}, {
  iconName: "shield",
  title: "Regulated Compliance Assurance",
  detail: "Validated for GMP environments with zero cross-contamination risk and fail-safe diversion valves."
}, {
  iconName: "gauge",
  title: "Minimal Disruption Setup",
  detail: "Direct plug-and-play connection to any major HPLC or UHPLC detector without software changes."
}];
export default function RelatedSolutions() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="relatedsolutions" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-t border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 mb-3">
              <Badge variant="outline" className="text-primary border-primary/30">
                <Recycle className="size-3 text-primary" />
                Sustainable Chromatography
              </Badge>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default">
              Related HPLC Solvent Recovery Solutions
            </h2>
            <p className="mt-3 text-base md:text-lg text-muted-foreground">
              Reduce laboratory operational costs, lower disposal footprint, and enhance analytical sustainability with proven hardware and qualification support.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/ChromatographyPage">
              <Button variant="outline" className="gap-2">
                All Chromatography
                <ArrowRight className="size-4" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Feature Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {solutions.map((item, i) => <motion.div key={item.id} variants={cardVariants} data-index={i} className="flex">
              <Card className="flex flex-col justify-between w-full overflow-hidden border border-border shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                <div>
                  <div className="relative aspect-16/10 overflow-hidden bg-muted">
                    <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3">
                      <Badge variant="secondary" className="font-semibold text-xs backdrop-blur-md bg-background/90 text-foreground border border-border">
                        {item.category}
                      </Badge>
                    </div>
                  </div>

                  <CardHeader className="pt-6">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-xs font-medium text-primary uppercase tracking-wider">
                        {item.badge}
                      </span>
                    </div>
                    <CardTitle className="text-xl font-bold text-foreground font-default">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <div className="flex items-center gap-2 p-3 rounded-md bg-secondary text-secondary-foreground text-xs font-medium border border-border/60">
                      <CheckCircle2 className="size-4 text-primary shrink-0" />
                      <span>{item.specs}</span>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-2">
                  <Link to={item.link} className="w-full">
                    <Button variant="default" className="w-full justify-between group">
                      <span>Explore Solution</span>
                      <ArrowRight className="size-4 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Benefits & Consultation Banner */}
        <div className="rounded-xl border border-border bg-secondary/50 p-6 sm:p-8 md:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <h3 className="text-xl md:text-2xl font-bold text-foreground font-default mb-6">
                Why Implement Antech Solvent Recovery Systems?
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {highlights.map((highlight, index) => <div key={index} data-index={index} className="flex flex-col gap-2">
                    <div className="size-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-1">
                      {highlight.iconName === "recycle" && <Recycle className="size-5" />}
                      {highlight.iconName === "shield" && <ShieldCheck className="size-5" />}
                      {highlight.iconName === "gauge" && <Gauge className="size-5" />}
                    </div>
                    <h4 className="font-semibold text-sm text-foreground font-default">
                      {highlight.title}
                    </h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {highlight.detail}
                    </p>
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-4 lg:border-l lg:border-border lg:pl-8 flex flex-col justify-center items-start">
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary mb-2">
                <Sparkles className="size-3.5" />
                <span>Tailored Application Support</span>
              </div>
              <p className="text-sm text-foreground font-medium mb-4">
                Calculate your return on investment and test your analytical methods with our chromatography team.
              </p>
              <div className="flex flex-col sm:flex-row lg:flex-col gap-3 w-full">
                <Link to="/BookADemoPage" className="w-full">
                  <Button variant="default" className="w-full">
                    Book a Demonstration
                  </Button>
                </Link>
                <Link to="/DiscussYourApplicationPage" className="w-full">
                  <Button variant="outline" className="w-full">
                    Discuss Your Application
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}